/**
 * Admin article editor — create or edit a Help Center guide.
 *
 * Live Markdown preview beside the editor. Only rendered for admins (the Help
 * Center gates the "New/Edit article" affordances behind isAdmin).
 */

import { useState } from 'react';
import { saveHelpArticle } from '@/api/endpoints/help.js';
import { useToast } from '@/components/ui/useToast.js';
import Markdown from './Markdown.jsx';

export default function ArticleEditor({ sections, article, defaultSection, onSaved, onCancel }) {
  const toast = useToast();
  const editing = Boolean(article?.id);
  const [form, setForm] = useState({
    id: article?.id,
    section: article?.section || defaultSection || 'getting-started',
    title: article?.title || '',
    summary: article?.summary || '',
    tags: (article?.tags || []).join(', '),
    order: article?.order ?? 1,
    published: article?.published ?? true,
    content: article?.content || '## Overview\n\nWrite the guide here. Use short steps.\n\n> [!TIP]\n> Callouts like this stand out.'
  });
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    if (!form.title.trim() || !form.content.trim()) {
      toast.warning('Missing details', 'A title and some content are required.');
      return;
    }
    setBusy(true);
    try {
      const saved = await saveHelpArticle({
        id: form.id,
        section: form.section,
        title: form.title.trim(),
        summary: form.summary.trim(),
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        order: Number(form.order) || 1,
        published: form.published,
        content: form.content
      });
      toast.success('Saved', `"${saved.title}" has been ${editing ? 'updated' : 'created'}.`);
      onSaved?.(saved);
    } catch (err) {
      toast.error('Could not save', err?.message || 'Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="help-breadcrumb">
        <button type="button" onClick={onCancel}>
          <i className="fas fa-arrow-left" aria-hidden="true" /> Back
        </button>
        <span>/</span>
        <span>{editing ? 'Edit article' : 'New article'}</span>
      </div>

      <div className="help-editor-grid">
        <div>
          <div className="help-field">
            <label htmlFor="ed-title">Title</label>
            <input id="ed-title" className="help-input" value={form.title} onChange={(e) => set({ title: e.target.value })} />
          </div>
          <div className="help-field">
            <label htmlFor="ed-section">Section</label>
            <select id="ed-section" className="help-select" value={form.section} onChange={(e) => set({ section: e.target.value })}>
              {(sections || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="help-field">
            <label htmlFor="ed-summary">
              Summary <span className="hint">— one line shown in lists &amp; search</span>
            </label>
            <input id="ed-summary" className="help-input" value={form.summary} onChange={(e) => set({ summary: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="help-field" style={{ flex: 2 }}>
              <label htmlFor="ed-tags">
                Tags <span className="hint">— comma separated</span>
              </label>
              <input id="ed-tags" className="help-input" value={form.tags} onChange={(e) => set({ tags: e.target.value })} />
            </div>
            <div className="help-field" style={{ width: 90 }}>
              <label htmlFor="ed-order">Order</label>
              <input id="ed-order" type="number" min="1" className="help-input" value={form.order} onChange={(e) => set({ order: e.target.value })} />
            </div>
          </div>
          <div className="help-field">
            <label className="help-check">
              <input type="checkbox" checked={form.published} onChange={(e) => set({ published: e.target.checked })} />
              <span>Published <span className="hint">(unticked = a draft only admins can see)</span></span>
            </label>
          </div>
          <div className="help-field">
            <label htmlFor="ed-content">
              Content <span className="hint">— Markdown; use ## headings, numbered steps, and &gt; [!TIP] callouts</span>
            </label>
            <textarea id="ed-content" className="help-textarea code" value={form.content} onChange={(e) => set({ content: e.target.value })} />
          </div>
          <div className="help-form-actions">
            <button type="button" className="help-btn-primary" onClick={save} disabled={busy}>
              <i className={`fas ${busy ? 'fa-spinner help-spin' : 'fa-floppy-disk'}`} aria-hidden="true" />
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Create article'}
            </button>
            <button type="button" className="help-btn-ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>

        <div>
          <div className="help-field" style={{ marginBottom: 6 }}>
            <label>Live preview</label>
          </div>
          <div className="help-editor-preview">
            <h3 style={{ margin: '4px 0 2px', fontSize: 20, color: 'var(--ink)' }}>{form.title || 'Untitled'}</h3>
            {form.summary && <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>{form.summary}</p>}
            <Markdown>{form.content}</Markdown>
          </div>
        </div>
      </div>
    </div>
  );
}
